import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function GalleryOverlay({ category, onClose }) {
    const images = category.images;
    const [selectedIndex, setSelectedIndex] = useState(null);

    /* Lock body scroll while overlay open */
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    /* Keyboard navigation */
    useEffect(() => {
        const handleKey = (e) => {
            if (selectedIndex === null) return;

            if (e.key === "Escape") setSelectedIndex(null);
            if (e.key === "ArrowRight")
                setSelectedIndex((i) => (i + 1) % images.length);
            if (e.key === "ArrowLeft")
                setSelectedIndex((i) => (i - 1 + images.length) % images.length);
        };

        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [selectedIndex, images.length]);

    return (
        <div className="fixed inset-0 bg-neutral-950 text-white z-50 overflow-y-auto">

            {/* HEADER */}
            <div className="sticky top-0 bg-neutral-950 z-20 flex justify-between items-center px-8 py-6 border-b border-white/10">
                <h2 className="text-2xl font-semibold tracking-tight">
                    {category.title}
                </h2>

                <button
                    onClick={onClose}
                    className="text-white/70 hover:text-white transition"
                >
                    <X size={28} />
                </button>
            </div>

            {/* DYNAMIC MASONRY */}
            <div className="max-w-7xl mx-auto px-6 py-12 columns-1 sm:columns-2 gap-x-6 space-y-6">

                {images.map((img, index) => (
                    <div
                        key={index}
                        onClick={() => setSelectedIndex(index)}
                        className="break-inside-avoid cursor-pointer overflow-hidden rounded-xl group"
                    >
                        <img
                            src={img.src}
                            alt=""
                            loading="lazy"
                            className="w-full h-auto transition duration-500 group-hover:opacity-90"
                        />
                    </div>
                ))}

            </div>

            {/* LIGHTBOX */}
            {selectedIndex !== null && (
                <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[60]">

                    {/* Close */}
                    <button
                        onClick={() => setSelectedIndex(null)}
                        className="absolute top-6 right-6 text-white/70 hover:text-white transition"
                    >
                        <X size={28} />
                    </button>

                    {/* Prev */}
                    <button
                        onClick={() =>
                            setSelectedIndex((i) => (i - 1 + images.length) % images.length)
                        }
                        className="absolute left-6 text-white/70 hover:text-white transition"
                    >
                        <ChevronLeft size={40} />
                    </button>

                    {/* Image + Description */}
                    <div className="max-w-6xl w-full px-6 text-center">
                        <img
                            src={images[selectedIndex].src}
                            alt=""
                            className="max-h-[85vh] w-auto mx-auto object-contain"
                        />

                        <div className="mt-8">
                            <h3 className="text-2xl font-semibold mb-3">
                                {images[selectedIndex].title}
                            </h3>
                            <p className="text-white/70 max-w-2xl mx-auto leading-relaxed">
                                {images[selectedIndex].description}
                            </p>
                        </div>
                    </div>

                    {/* Next */}
                    <button
                        onClick={() =>
                            setSelectedIndex((i) => (i + 1) % images.length)
                        }
                        className="absolute right-6 text-white/70 hover:text-white transition"
                    >
                        <ChevronRight size={40} />
                    </button>

                </div>
            )}

        </div>
    );
}